import React, { useContext } from 'react'
import { Select, MenuItem, InputLabel, FormControl, TextField, Autocomplete } from '@mui/material'
import { LoadingContext } from '../M_Utils'
import PropTypes from 'prop-types'

export function MSelect({
  label, value, options, color, secondColor, disabled,
  required, onChange, onClick, multiple, minWidth, itemTemplate, height,
  margin, padding, fontSize, marginLeft, marginRight, size, autocomplete,
  onChangeValue, noNone,
  // allow upstream to control remote filtering & opening hooks + listbox
  onInputChange,
  onOpen,
  ListboxProps,
  // allow upstream to disable client filtering (server-driven lists)
  filterOptions,

  // allow upstream to control input text & blur/close behavior (Autocomplete only)
  inputValue,
  clearOnBlur,
  onClose,
}) {
  const ctx = useContext(LoadingContext)

  color = color || ctx.primaryColor || 'var(--color-black)'
  secondColor = secondColor || ctx.secondaryColor || 'var(--color-black)'

  const styles = {
    margin: margin,
    width: size || null,
    marginLeft: marginLeft,
    marginRight: marginRight,
    '& label': { color: 'var(--color-black)' },
    '& label.Mui-focused': { color: 'var(--color-black)' },
    '& .MuiInput-underline:after': { borderBottomColor: color },
    "& .MuiInputBase-root.Mui-disabled": {
      "& > fieldset": {
        borderColor: 'rgba(0, 0, 0, 0.38) !important'
      }
    },
    '& .MuiOutlinedInput-input': {
      backgroundColor: 'var(--color-white)',
      padding: padding,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: color,
        width: size || 'auto',
        height: height,
      },
      '&:hover fieldset': {
        borderColor: color,
      },
      '&.Mui-focused fieldset': {
        borderColor: color,
        boxShadow: `2px 2px 2px ${color}`
      },
      "& legend": {
        // letterSpacing: -1.9,
      }
    },
  }

  return (
    <>
      {autocomplete ?
        <Autocomplete
          id="combo-box-demo"
          options={options}
          sx={styles}
          getOptionLabel={(e) => e?.label || ''}
          multiple={multiple}
          value={value}
          disabled={disabled}

          // allow upstream to control the text shown in the input (controlled mode)
          {...(inputValue !== undefined ? { inputValue } : {})}

          // allow upstream to prevent losing typed text on blur
          {...(clearOnBlur !== undefined ? { clearOnBlur } : {})}

          //  wire upstream change of the input text (for remote fetch)
          onInputChange={onInputChange}
          //  wire open hook (to trigger initial load)
          onOpen={onOpen}
          // allow upstream to react when dropdown closes (ex: reset flags)
          onClose={onClose}

          // Use upstream ListboxProps if provided (scroll listener, max height, etc.)
          ListboxProps={ListboxProps || { style: { maxHeight: 196 } }}
          //  equality is by 'value' (Id fallback) to avoid false duplicates
          isOptionEqualToValue={(option, val) => {
            if (!option && !val) return true
            if (!option || !val) return false
            const ov = option.value ?? option.Id
            const vv = val.value ?? val.Id
            return String(ov) === String(vv)
          }}
          // Allow disabling client-side filter (server-driving options)
          filterOptions={filterOptions}
          onChange={(event, v /* MUI passes full option object(s) */, reason) => onChangeValue?.(v, reason)}
          renderInput={(params) => (
            <TextField
              {...params}
              slotProps={{ inputLabel: { shrink: true } }}
              margin="dense"
              required={required}
              label={label}
              variant="outlined"
              style={{ height: height, textOverflow: 'ellipsis' }}
              sx={styles}
              size="small"
              id="margin-none"
              disabled={disabled}
            />
          )}
        />
        :
        <FormControl
          sx={{
            margin: margin,
            '& label': { color: secondColor },
            '& label.Mui-focused': { color: secondColor },
            '& .MuiInput-underline:after': { color: color, borderColor: "transparent" },
            "& .MuiInputBase-root.Mui-disabled": {
              "& > fieldset": {
                borderColor: 'rgba(0, 0, 0, 0.38) !important'
              }
            },
            '& .MuiOutlinedInput-root': {
              height: height,
              fontSize: fontSize,
              padding: padding,
              '& fieldset': {
                borderColor: color,
              },
              '&:hover fieldset': {
                borderColor: color,
              },
              '&.Mui-focused fieldset': {
                borderColor: color,
                boxShadow: `2px 2px 2px ${color}`
              },
            },
            minWidth: minWidth,
          }}
          margin="dense"
          fullWidth
          size='small'
          required={required}
          id="margin-none"
          disabled={disabled}
          data-testid="form-control"
        >
          <InputLabel shrink>{label}</InputLabel>
          <Select
            notched={true}
            label={label}
            value={value}
            disabled={disabled}
            required={required}
            onChange={onChange}
            multiple={multiple}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 155,
                  "& .Mui-selected": {
                    backgroundColor: "var(--color-grey-light)",
                    height: height
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "var(--color-grey-light)"
                  },
                }
              }
            }}
          >
            {!noNone && <MenuItem value="" onClick={() => onClick ? onClick({}) : {}}><em>None</em></MenuItem>}
            {options.map((el, index) => (
              <MenuItem
                key={el?.value ?? el?.label ?? index}
                disableRipple={true}
                value={el.value}
                onClick={() => onClick ? onClick(el) : {}}
              >
                {itemTemplate ? itemTemplate(el) : el.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>}
    </>
  )
}

MSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  options: PropTypes.array,
  color: PropTypes.string,
  secondColor: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  onChange: PropTypes.func,
  onClick: PropTypes.func,
  multiple: PropTypes.bool,
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  itemTemplate: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  margin: PropTypes.string,
  padding: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginRight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  autocomplete: PropTypes.bool,
  onChangeValue: PropTypes.func,
  noNone: PropTypes.bool,
  onInputChange: PropTypes.func,
  onOpen: PropTypes.func,
  ListboxProps: PropTypes.any,
  filterOptions: PropTypes.func,

  // Autocomplete controlled input support
  inputValue: PropTypes.string,
  clearOnBlur: PropTypes.bool,
  onClose: PropTypes.func,
}
